/**
 * PendingSyncBadge -- live count of queued mutations.
 *
 * Module 2.7 Phase 3 Part 1. Mounts in the Layout sidebar; reads from
 * the IndexedDB sync queue via ``useSyncQueueCount``. Hidden when
 * the queue is empty so the chrome stays clean in the common case.
 */
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { CloudOff } from 'lucide-react'
import { useSyncQueueCount } from '@/hooks/useSyncQueue'


export default function PendingSyncBadge() {
  const navigate = useNavigate()
  const { i18n } = useTranslation()
  const isArabic = i18n.language === 'ar'
  const { count, entries, failed } = useSyncQueueCount()

  if (count === 0) return null

  const hasFailed = failed.length > 0
  const tooltip = entries
    .slice(0, 5)
    .map((e) => e.label || e.kind)
    .join('\n')

  // Failed entries (server-rejected, awaiting user decision) shift
  // the colour from amber -> rose to nudge the user toward
  // /sync/pending.
  const palette = hasFailed
    ? 'bg-rose-500/15 text-rose-300 border-rose-500/30 hover:bg-rose-500/25'
    : 'bg-amber-500/15 text-amber-300 border-amber-500/30 hover:bg-amber-500/25'

  return (
    <button
      type="button"
      title={tooltip}
      onClick={() => navigate('/sync/pending')}
      className={
        'inline-flex items-center gap-1.5 px-2 py-1 rounded-md ' +
        'text-xs font-medium border transition ' + palette
      }
      role="status"
      aria-live="polite"
    >
      <CloudOff className="w-3.5 h-3.5" />
      <span>
        {hasFailed
          ? (isArabic
              ? `${failed.length} مرفوض${failed.length === 1 ? '' : 'ة'}`
              : `${failed.length} need${failed.length === 1 ? 's' : ''} review`)
          : (isArabic
              ? `${count} في الانتظار`
              : `${count} pending sync`)}
      </span>
    </button>
  )
}
